"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { useScrollReveal } from "@/hooks/use-scroll-reveal";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { useSiteFlavor } from "@/components/layout/site-flavor-provider";

const FAQ_KEYS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;

export function LandingFaq() {
  const t = useTranslations("landing");
  const { isGetmint } = useSiteFlavor();
  const ref = useScrollReveal<HTMLElement>({ animation: "fade-up" });
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const keys = isGetmint ? FAQ_KEYS.filter((n) => n !== 10) : FAQ_KEYS;

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-24" ref={ref} id="faq">
      <div className="mx-auto max-w-3xl px-6">
        <h2 className="text-center text-3xl font-bold tracking-tight text-ink sm:text-4xl">
          {t("faq.title")}
        </h2>
        <p className="mt-4 text-center text-landing-muted">
          {t("faq.subtitle")}
        </p>

        <div className="mt-12 space-y-3">
          {keys.map((n) => {
            const isOpen = openIndex === n;
            return (
              <div
                key={n}
                className="rounded-2xl border border-landing-border bg-landing-card"
              >
                <button
                  onClick={() => toggle(n)}
                  className="flex w-full items-center justify-between px-6 py-5 text-left"
                >
                  <span className="text-base font-medium text-ink pr-4">
                    {t(`faq.q${n}`)}
                  </span>
                  <ChevronDown
                    className={cn(
                      "h-5 w-5 shrink-0 text-landing-muted transition-transform duration-200",
                      isOpen && "rotate-180"
                    )}
                  />
                </button>
                <div
                  className={cn(
                    "grid transition-all duration-200 ease-in-out",
                    isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                  )}
                >
                  <div className="overflow-hidden">
                    <p className="px-6 pb-5 text-sm leading-relaxed text-landing-muted">
                      {isGetmint
                        ? t(`faq.a${n}`).replaceAll("Mint24", "Mint").replaceAll("mint24.app", "getmintai.app")
                        : t(`faq.a${n}`)}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
